import styled from '@emotion/styled'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Exercise, Intensity } from '../../types/exercise.type'
import { X } from 'lucide-react'
import { IconButton } from '@mui/material'
import Breakpoint from '../../Breakpoint'
import { renderHtmlContent } from '../../utils/htmlRenderer'

interface ExerciseDetailsModalProps {
    show: boolean
    onClose: () => void
    exercise: Exercise | null
}

const ExerciseDetailsModal: React.FC<ExerciseDetailsModalProps> = ({
    show,
    onClose,
    exercise,
}) => {
    const { t } = useTranslation()

    if (!exercise) return null

    const getIntensityLabel = (intensity: string) => {
        const labels: Record<string, string> = {
            LOW: t('coach.exercises.intensityLow'),
            MEDIUM: t('coach.exercises.intensityMedium'),
            HIGH: t('coach.exercises.intensityHigh'),
        }
        return labels[intensity] || intensity
    }

    const getGameTypeLabel = (gameType: string) => {
        const labels: Record<string, string> = {
            SINGLES: t('coach.exercises.gameTypeSingles'),
            DOUBLES: t('coach.exercises.gameTypeDoubles'),
            BOTH: t('coach.exercises.gameTypeBoth'),
        }
        return labels[gameType] || gameType
    }

    const getCourtTypeLabel = (courtType: string) => {
        const labels: Record<string, string> = {
            FULL: t('coach.exercises.courtTypeFull'),
            HALF: t('coach.exercises.courtTypeHalf'),
            BOTH: t('coach.exercises.courtTypeBoth'),
        }
        return labels[courtType] || courtType
    }

    // Filtrer les compétences : afficher uniquement les parents ou les enfants sans parent
    const getDisplayedSkills = () => {
        if (!exercise.skills || exercise.skills.length === 0) return []
        
        const parentSkills = new Map<number, string>()
        const childSkillsWithoutParent: Array<{ id: number; name: string }> = []
        
        exercise.skills.forEach((skillItem) => {
            if (skillItem.skill.parentSkill) {
                // Si la compétence a un parent, on ajoute le parent
                parentSkills.set(
                    skillItem.skill.parentSkill.id,
                    skillItem.skill.parentSkill.name
                )
            } else {
                // Si la compétence n'a pas de parent, on l'ajoute directement
                childSkillsWithoutParent.push({
                    id: skillItem.skill.id,
                    name: skillItem.skill.name,
                })
            }
        })
        
        // Combiner les parents et les enfants sans parent
        return [
            ...Array.from(parentSkills.entries()).map(([id, name]) => ({ id, name })),
            ...childSkillsWithoutParent,
        ]
    }

    const displayedSkills = getDisplayedSkills()

    return (
        <StyledModal show={show} onHide={onClose} size="lg" centered>
            <ModalHeader>
                <ModalTitle>{exercise.title}</ModalTitle>
                <IconButton onClick={onClose} size="small">
                    <X size={20} />
                </IconButton>
            </ModalHeader>
            <ModalBody>
                <MetaContainer>
                    <MetaItem>
                        <b>👥</b> {exercise.peopleCount}{' '}
                        {t('coach.exercises.exerciseItem.people', {
                            count: exercise.peopleCount,
                            defaultValue: 'personne',
                            defaultValue_plural: 'personnes',
                        })}
                    </MetaItem>
                    <MetaItem>
                        <b>⚡</b> {t('coach.exercises.exerciseItem.intensity')}{' '}
                        <IntensityBadge intensity={exercise.intensity}>
                            {t(`coach.exercises.exerciseItem.intensityEnum.${exercise.intensity}`)}
                        </IntensityBadge>
                    </MetaItem>
                    <MetaItem>
                        <b>🏸</b> {t('coach.exercises.exerciseItem.gameType')} {getGameTypeLabel(exercise.gameType)}
                    </MetaItem>
                    <MetaItem>
                        <b>📏</b> {t('coach.exercises.exerciseItem.courtType')} {getCourtTypeLabel(exercise.courtType)}
                    </MetaItem>
                    {displayedSkills.length > 0 && (
                        <MetaItem>
                            <b>🎯</b> {t('coach.exercises.exerciseItem.skills')}
                            <SkillsList>
                                {displayedSkills.map((skill) => (
                                    <SkillBadge key={skill.id}>{skill.name}</SkillBadge>
                                ))}
                            </SkillsList>
                        </MetaItem>
                    )}
                </MetaContainer>

                {exercise.description && (
                    <DescriptionSection
                        dangerouslySetInnerHTML={renderHtmlContent(exercise.description)}
                    />
                )}
            </ModalBody>
        </StyledModal>
    )
}

const StyledModal = styled(Modal)`
    .modal-content {
        border-radius: 10px;
        border: none;
    }
`

const ModalHeader = styled(Modal.Header)`
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
`

const ModalTitle = styled.h3`
    margin: 0;
    font-size: 2rem;
    color: var(--primary-color, #b40000);
    font-weight: 600;
`

const ModalBody = styled(Modal.Body)`
    padding: 20px 24px;
`

const MetaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 1.4rem;
    flex-wrap: wrap;
`

interface IntensityBadgeProps {
    intensity: Intensity
}

interface IntensityStyles {
    bg: string
    color: string
}

const getIntensityStyles = (intensity: Intensity): IntensityStyles => {
    switch (intensity) {
        case Intensity.LOW:
            return { bg: '#e6f7e6', color: '#2e7d32' }
        case Intensity.MEDIUM:
            return { bg: '#fff3e0', color: '#e65100' }
        case Intensity.HIGH:
            return { bg: '#ffebee', color: '#c62828' }
    }
}

const IntensityBadge = styled.span<IntensityBadgeProps>(({ intensity }) => {
    const styles = getIntensityStyles(intensity)
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontWeight: 500,
        backgroundColor: styles.bg,
        color: styles.color,
    }
})

const SkillsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
`

const SkillBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f5f5f5;
    padding: 2px 10px;
    border-radius: 12px;
    color: #616161;
    font-size: 1.4rem;
`

const DescriptionSection = styled.div`
    margin-top: 16px;
    color: #555;
    line-height: 1.5;
    font-size: 1.4rem;

    p {
        margin: 0 0 1em 0;
        &:last-child {
            margin-bottom: 0;
        }
    }

    ul,
    ol {
        margin: 0.5em 0;
        padding-left: 1.5em;

        li {
            margin-bottom: 0.5em;
        }
    }

    a {
        color: var(--primary-color, #b40000);
        text-decoration: none;
        word-break: break-all;

        &:hover {
            text-decoration: underline;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        color: var(--primary-color, #b40000);
        margin-top: 1em;
        margin-bottom: 0.5em;
        font-weight: 600;
    }

    strong,
    b {
        font-weight: 600;
        color: #222;
    }

    em,
    i {
        font-style: italic;
    }

    blockquote {
        border-left: 3px solid var(--primary-color, #b40000);
        padding-left: 1em;
        margin: 1em 0;
        color: #555;
    }
`

export default ExerciseDetailsModal
